import { useState } from "react";
import { X, Plus, Trash2, LayoutTemplate, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const FIELD_TYPES = [
  { v: "text", l: "Texto" },
  { v: "number", l: "Número" },
  { v: "select", l: "Lista" },
  { v: "date", l: "Fecha" },
];

export const BoardSettings = ({ board, onClose, onUpdated }) => {
  const [fields, setFields] = useState(board.custom_fields || []);
  const [fName, setFName] = useState("");
  const [fType, setFType] = useState("text");
  const [fOptions, setFOptions] = useState("");
  const [tplName, setTplName] = useState("");
  const [saving, setSaving] = useState(false);
  const [gitUrl, setGitUrl] = useState("");

  const addField = () => {
    if (!fName.trim()) return;
    const f = { id: crypto.randomUUID(), name: fName.trim(), type: fType, options: fType === "select" ? fOptions.split(",").map((o) => o.trim()).filter(Boolean) : [] };
    setFields((fs) => [...fs, f]);
    setFName(""); setFOptions("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/boards/${board.id}`, { custom_fields: fields });
      onUpdated(data);
      toast.success("Campos guardados");
      onClose();
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) return;
    try {
      await api.post("/board-templates", { name: tplName, board_id: board.id });
      toast.success("Plantilla guardada");
      setTplName("");
    } catch { toast.error("No se pudo guardar la plantilla"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#121214] border border-white/10 rounded-2xl p-6 animate-fade-up max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="board-settings">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold">Configuración del tablero</h2>
          <button onClick={onClose} data-testid="close-board-settings"><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
        </div>

        <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Cliente</label>
        <input data-testid="board-client-input" defaultValue={board.client || ""} placeholder="Cliente asociado (opcional)"
          onBlur={async (e) => { try { const { data } = await api.put(`/boards/${board.id}`, { client: e.target.value }); onUpdated(data); } catch {} }}
          className="mt-2 mb-5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />

        <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Campos personalizados</label>
        <div className="space-y-2 mt-2 mb-3">
          {fields.map((f) => (
            <div key={f.id} className="flex items-center gap-3 bg-[#0A0A0B] rounded-lg px-3 py-2" data-testid={`field-row-${f.id}`}>
              <span className="text-sm font-medium flex-1">{f.name}</span>
              <span className="text-xs text-zinc-500">{FIELD_TYPES.find((t) => t.v === f.type)?.l}{f.type === "select" && ` (${f.options.join(", ")})`}</span>
              <button onClick={() => setFields((fs) => fs.filter((x) => x.id !== f.id))} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {fields.length === 0 && <p className="text-sm text-zinc-600">Sin campos. Añade uno (ej. Cliente, Cantidad).</p>}
        </div>
        <div className="flex gap-2">
          <input data-testid="field-name-input" value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Nombre del campo"
            className="flex-1 bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
          <select data-testid="field-type-select" value={fType} onChange={(e) => setFType(e.target.value)}
            className="bg-[#0A0A0B] border border-white/10 rounded-lg px-2 py-2 text-sm outline-none focus:border-primary">
            {FIELD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <button onClick={addField} data-testid="add-field-button" className="bg-white/5 hover:bg-white/10 rounded-lg px-3"><Plus className="h-4 w-4" /></button>
        </div>
        {fType === "select" && (
          <input data-testid="field-options-input" value={fOptions} onChange={(e) => setFOptions(e.target.value)} placeholder="Opciones separadas por coma"
            className="mt-2 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
        )}
        <button onClick={save} disabled={saving} data-testid="save-fields-button"
          className="mt-4 w-full bg-primary hover:bg-[#FF5047] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar campos
        </button>

        <div className="border-t border-white/10 mt-6 pt-5">
          <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><LayoutTemplate className="h-3 w-3" /> Guardar como plantilla</label>
          <p className="text-xs text-zinc-600 mt-1">Reutiliza columnas, campos y tareas de este tablero en nuevos proyectos.</p>
          <div className="flex gap-2 mt-2">
            <input data-testid="template-name-input" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Nombre de la plantilla"
              className="flex-1 bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
            <button onClick={saveTemplate} data-testid="save-template-button" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 text-sm">Guardar</button>
          </div>
        </div>
        <div className="border-t border-white/10 mt-6 pt-5">
          <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Integración GitHub / GitLab</label>
          <p className="text-xs text-zinc-600 mt-1">Pega esta URL como webhook (push events) en tu repositorio y los commits aparecerán en el chat del tablero.</p>
          {gitUrl ? (
            <div className="bg-black/40 rounded-lg p-3 text-xs flex items-center gap-2 mt-2" data-testid="git-webhook-url">
              <code className="flex-1 break-all">{gitUrl}</code>
              <button onClick={() => { navigator.clipboard.writeText(gitUrl); toast.success("Copiado"); }} className="text-zinc-400 hover:text-white text-xs shrink-0">Copiar</button>
            </div>
          ) : (
            <button onClick={async () => { const { data } = await api.post(`/boards/${board.id}/git-webhook`); setGitUrl(process.env.REACT_APP_BACKEND_URL + data.webhook_path); }}
              data-testid="get-git-webhook" className="mt-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm">Obtener URL del webhook</button>
          )}
        </div>
      </div>
    </div>
  );
};
